import { formatDate } from 'src/utils/formatDate';
import { formatMoney } from 'src/utils/formatMoney';
import { IDealerData } from 'src/utils/interfaces/user';
import { useLocalization } from 'src/utils/useLocalization';
import {
    IEFTNotifications,
    RemittanceInfo,
} from 'src/services/models/funding.service';
import React, { Dispatch, SetStateAction } from 'react';

interface EftContentProps {
    eftResponse: IEFTNotifications[];
    contentIndex: number;
    setContentIndex: Dispatch<SetStateAction<number>>;
    dealerDataInfo?: IDealerData;
    tableContent: RemittanceInfo[];
}

export const handleDateTrackerClick = (
    direction: 'up' | 'down',
    contentIndex: number,
    contentLength: number,
    setContentIndex: Dispatch<SetStateAction<number>>,
) => {
    const increment = 1;
    const defaultIndex = 0;
    if (!contentLength) return;
    switch (direction) {
        case 'up':
            if (contentIndex + increment < contentLength) {
                setContentIndex(contentIndex + increment);
            } else {
                setContentIndex(defaultIndex);
            }
            break;
        case 'down':
            if (!!contentIndex) {
                setContentIndex(contentIndex - increment);
            } else {
                setContentIndex(contentLength - increment);
            }
            break;
    }
};

export const financialServicesText = (brand: string | undefined, eft: any) => {
    if (brand?.toLowerCase() === 'vw') {
        return eft.EFT_VW;
    }
    if (brand?.toLowerCase() === 'audi') {
        return eft.EFT_AUDI;
    }
};

export const EftContent: React.FC<EftContentProps> = ({
    eftResponse,
    contentIndex,
    setContentIndex,
    dealerDataInfo,
    tableContent,
}) => {
    const { eft } = useLocalization();

    return (
        <div className='eft-content'>
            <div className='eft-content-upper-container'>
                <div className='date-tracker'>
                    <div title='date selected'>
                        {formatDate(eftResponse[contentIndex].OperationDate)}
                    </div>
                    <div className='date-tracker-action-container'>
                        <div
                            className='date-tracker-button'
                            onClick={() =>
                                handleDateTrackerClick(
                                    'down',
                                    contentIndex,
                                    eftResponse.length,
                                    setContentIndex,
                                )
                            }
                            title='decrement date'
                        >
                            <i
                                className={
                                    'c-icon c-icon--[chevron-left] c-icon--small eft-icon'
                                }
                            ></i>
                        </div>
                        <div
                            className='date-tracker-button'
                            onClick={() =>
                                handleDateTrackerClick(
                                    'up',
                                    contentIndex,
                                    eftResponse.length,
                                    setContentIndex,
                                )
                            }
                            title='increment date'
                        >
                            <i
                                className={
                                    'c-icon c-icon--[chevron-right] c-icon--small eft-icon'
                                }
                            ></i>
                        </div>
                    </div>
                </div>
                <div className='text'>
                    {`${eftResponse.length} ${eft.EFT_DAYS_WITH_NOTIFICATIONS}`}
                </div>
            </div>
            <div className='eft-table-container'>
                <div>
                    <div className='dealer-info'>
                        <span className='bold'>{`${dealerDataInfo?.dealer.dealerName} ${dealerDataInfo?.dealer.dealerNumber}`}</span>
                        {` | ${financialServicesText(
                            dealerDataInfo?.dealer.brand,
                            eft,
                        )}`}
                    </div>
                    <div className='account-disclosure'>
                        {`${eft.EFT_DEPOSIT_PART1} ${eftResponse[contentIndex].BankAccount} ${eft.EFT_AT}
                                ${eftResponse[contentIndex].BankName} ${eft.EFT_DEPOSIT_PART2}`}
                    </div>
                </div>
                <table className='eft-table'>
                    <thead className='eft-table-head'>
                        <tr>
                            <td className='eft-table-head-cell-remit'>
                                {eft.EFT_REMITTANCE_INFO}
                            </td>
                            <td className='eft-table-head-cell-reserve'>
                                {eft.EFT_RESERVE_AMOUNT}
                            </td>
                            <td className='eft-table-head-cell-amount'>
                                {eft.EFT_AMOUNT_PAID}
                            </td>
                        </tr>
                    </thead>
                    <tbody className='eft-table-body'>
                        {tableContent.map((row) => {
                            return (
                                <tr
                                    key={row.RemitanceDescription}
                                    className='eft-table-row'
                                >
                                    <td className='eft-table-cell-remit'>
                                        {row.RemitanceDescription}
                                    </td>
                                    <td className='eft-table-cell-reserve'>
                                        {formatMoney(row.ReserveAmount)}
                                    </td>
                                    <td className='eft-table-cell-amount'>
                                        {formatMoney(row.AmountPaid)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr className='eft-table-row-footer'>
                            <td>{eft.EFT_TOTAL}</td>
                            <td className='eft-table-cell-reserve'>
                                {formatMoney(
                                    eftResponse[contentIndex]
                                        .ReserveAmountTotal,
                                )}
                            </td>
                            <td className='eft-table-cell-amount'>
                                {formatMoney(
                                    eftResponse[contentIndex]
                                        .FundingNonLeaseTotal,
                                )}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};
